import type { BoardCount, Entry, EntryPart, ScoreInput, SpaceEvent } from "@repo/shared-types";
import type { Read } from "./event-form";
import { partLabel } from "./event-form";
import { readScore, scoreDisplay, type Measured } from "./measures";
import { readTeamPeople, type TeamPeople } from "./teams";

/**
 * Results as the web app handles them: which parts a form records, reading it
 * into what the api takes, and which leaderboard a page opens on.
 *
 * The api checks every result again against how its part is scored; checking
 * here is only so a message can name the part as the form does.
 */

/**
 * The levels, each its own leaderboard. Rx+ is not one: it is a badge on a
 * result at Rx, which ranks with Rx (rxPlus).
 */
export const LEVELS = [
  { value: "rx", label: "Rx" },
  { value: "scaled", label: "Scaled" },
] as const;

/** Men and women, each its own leaderboard. Open was a third until 0025. */
export const DIVISIONS = [
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
] as const;

/**
 * A part's board of every division at once (0089), the last of its division
 * tabs. Nobody is in it, and no count names it: its count is its level's.
 * The overall ranking has none.
 */
export const ALL_DIVISIONS = { value: "all", label: "All" } as const;

/**
 * How a part felt to its athlete: theirs to say, never somebody else's
 * reaction to it.
 */
export const FEELS = [
  { value: "thumbs_down", label: "Thumbs down", emoji: "👎" },
  { value: "thumbs_up", label: "Thumbs up", emoji: "👍" },
  { value: "double_thumbs_up", label: "Double thumbs up", emoji: "👍👍" },
] as const;

/** A feel as it shows. The set grows by addition: an unknown one shows as it came. */
export function feelEmoji(feel: string | null): string {
  return FEELS.find((option) => option.value === feel)?.emoji ?? feel ?? "";
}

/** The longest note on a part, as the api holds it. */
export const MAX_NOTE = 2000;

/**
 * A level as a person reads it — Rx+ for a result at Rx with the badge. The
 * set grows by addition: an unknown one shows as it came.
 */
export function levelLabel(level: string | null, rxPlus = false): string {
  if (rxPlus && level === "rx") return "Rx+";
  return LEVELS.find((option) => option.value === level)?.label ?? level ?? "";
}

export function divisionLabel(division: string | null): string {
  return DIVISIONS.find((option) => option.value === division)?.label ?? division ?? "";
}

/**
 * Whether a result can go on a board. Every board is men's or women's, so it
 * needs a division: the profile's, which sharing copies in, or the one it was
 * last saved with. A part done in teams needs none of its own — a team's is
 * worked out from its people's. The api refuses the rest with a 409.
 */
export function canShareInDivision(
  parts: { isTeam: boolean; division?: string | null }[],
  profileDivision: string | null,
): boolean {
  return profileDivision !== null || parts.every((part) => part.isTeam || Boolean(part.division));
}

/**
 * A part a result form records: an event's part when registering from its
 * page, or the athlete's copy of one when editing from the log. Either way it
 * says how it is scored, and what the athlete has on it so far.
 */
export type RecordablePart = {
  /** The event part's id, or the entry part's: whichever the api is sent. */
  id: string;
  label: string;
  title: string;
  summary: string;
  usesLevels: boolean;
  timeLimitMs: number | null;
  types: Measured[];
  /** The athlete's copy, when they have one: its boxes and level pre-fill. */
  current?: EntryPart;
  /**
   * Whether a change now would wait for verification: the part has a
   * leaderboard and the api says its deadline has passed. Only an event's
   * page knows; the log cannot read a deadline.
   */
  late?: boolean;
  /**
   * Done in teams (0027): its result is each team's, so the form records
   * one only by forming a team, and keeps no note of its own — a teammate's
   * note is per team, on the team's page.
   */
  isTeam: boolean;
};

/**
 * Whether results for an event, or an entry of one, take one level for the
 * whole of it rather than one per part: it is ranked overall, and some part
 * has levels.
 */
export function oneLevel(of: { ranking: string; parts: { usesLevels: boolean }[] }): boolean {
  return of.ranking !== "none" && of.parts.some((part) => part.usesLevels);
}

/** Whether a part is scored at all: a part that is not has nothing to record. */
export function isScored(part: { types: Measured[] }): boolean {
  return part.types[0] !== undefined && part.types[0].measure !== "none";
}

/** An event's parts as its page's result form records them. */
export function eventParts(event: SpaceEvent, entry: Entry | null): RecordablePart[] {
  return event.parts.map((part, index) => ({
    id: part.id,
    label: partLabel(index),
    title: part.title,
    summary: part.summary,
    usesLevels: part.usesLevels,
    timeLimitMs: part.timeLimitMs,
    types: part.scores,
    current: entry?.parts.find((copy) => copy.partId === part.id),
    late: part.hasLeaderboard && part.deadlinePassed,
    isTeam: part.isTeam,
  }));
}

/** An entry's own parts, as the log's edit form records them. */
export function entryParts(entry: Entry): RecordablePart[] {
  return entry.parts.map((part, index) => ({
    id: part.id,
    label: partLabel(index),
    title: part.title,
    summary: part.summary,
    usesLevels: part.usesLevels,
    timeLimitMs: part.timeLimitMs,
    types: part.scores,
    current: part,
    isTeam: part.isTeam,
  }));
}

/**
 * One part as the api is sent it: its id (the event part's, or the entry
 * part's in the log), its level, its Rx+ badge — on or off for a part with
 * levels, off unless its level is Rx, and null for one without, which leaves
 * it be — its scores, and a team formed with it on a part done in teams.
 */
export type PartSent = {
  id: string;
  level: string | null;
  rxPlus: boolean | null;
  scores: ScoreInput[];
  /** A team formed with this result, on a team part; null otherwise. */
  team: (TeamPeople & { id: string }) | null;
};

/** A part's note and feel, "" for none: sending them replaces both. */
export type NoteSent = { id: string; note: string; feel: string };

/** One part as its dialog reads it: what is sent for it, and its note and feel — none on a team part. */
export type PartRead = { part: PartSent; note: NoteSent | null };

/**
 * One part's dialog, its boxes named parts.N.… — parts.N.id, parts.N.level,
 * parts.N.rxPlus, parts.N.note, parts.N.feel, parts.N.scores.0.min and so
 * on; on a team part, parts.N.team.open, .id and the teammates box. A part
 * left empty is read empty, which leaves it unrecorded; the level the dialog
 * pre-selected is dropped with it. With one level for the whole event, the
 * part carries none of its own.
 * `level` is the event's one level, when it takes one: the Rx+ badge rides
 * on Rx alone. Left null, it is not chosen yet, and the badge is read as it
 * was ticked; whoever sends the result clears it if the level is not Rx.
 */
export function readPart(
  form: FormData,
  part: RecordablePart,
  index: number,
  { single, level }: { single: boolean; level: string | null },
): Read<PartRead> {
  const text = (name: string) => String(form.get(name) ?? "").trim();
  const prefix = `parts.${index}`;
  const id = text(`${prefix}.id`) || part.id;
  // Open with nothing typed in it at all is a part skipped, like any other.
  // A unit is always picked, so it says nothing about whether anything was.
  const typed = (name: string) =>
    [...form.keys()].some(
      (key) => key.startsWith(name) && !key.endsWith(".unit") && String(form.get(key) ?? "").trim() !== "",
    );
  const forming =
    part.isTeam &&
    form.get(`${prefix}.team.open`) !== null &&
    (typed(`${prefix}.scores.`) || form.getAll(`${prefix}.team.invite`).length > 0 || typed(`${prefix}.team.guests.`));
  if (part.isTeam && !forming) {
    const empty = { value: null, reps: null, capped: null, unit: null };
    return { value: { part: { id, level: null, rxPlus: null, scores: part.types.map(() => empty), team: null }, note: null } };
  }
  const note = text(`${prefix}.note`);
  if (note.length > MAX_NOTE) return { error: `Part ${part.label}: a note is at most ${MAX_NOTE} characters.`, field: `${prefix}.note` };
  const scores: ScoreInput[] = [];
  for (const [position, type] of part.types.entries()) {
    const score = readScore(form, `${prefix}.scores.${position}`, type, `Part ${part.label}`);
    if (score.error !== undefined) return score;
    // Every field spelled, as the wire type has them: null is "not sent".
    scores.push({
      value: score.value.value ?? null,
      reps: score.value.reps ?? null,
      capped: score.value.capped ?? null,
      unit: score.value.unit ?? null,
    });
  }
  const recorded = scores[0] !== undefined && (scores[0].value !== null || scores[0].capped === true);
  if (!recorded && scores.some((score) => score.value !== null)) {
    return { error: `Part ${part.label}: give its first score before its tiebreaks.`, field: `${prefix}.scores.0` };
  }
  const partLevel = !single && part.usesLevels && recorded ? text(`${prefix}.level`) || null : null;
  // The badge rides on Rx alone: the part's level, or the event's one.
  const atRx = single ? level === null || level === "rx" : partLevel === "rx";
  let team: PartSent["team"] = null;
  if (forming) {
    if (!recorded) return { error: `Part ${part.label}: record the team's score to start the team.`, field: `${prefix}.scores.0` };
    const people = readTeamPeople(form, prefix, `Part ${part.label}`);
    if (people.error !== undefined) return people;
    team = { id: text(`${prefix}.team.id`), ...people.value };
  }
  return {
    value: {
      part: {
        id,
        level: partLevel,
        rxPlus: part.usesLevels ? atRx && form.get(`${prefix}.rxPlus`) !== null : null,
        scores,
        team,
      },
      note: part.isTeam ? null : { id, note, feel: text(`${prefix}.feel`) },
    },
  };
}

/**
 * A part read by its sheet while recording and kept on the page until the
 * bar sends the workout (Register workout, then Save changes): what was
 * read, or that its result is to be removed.
 */
export type PartDraft = PartRead & { removed: boolean };

/** A part whose result is to go: sent empty, its level, badge, note and feel cleared. */
export function removalOf(part: RecordablePart): PartDraft {
  const empty = { value: null, reps: null, capped: null, unit: null };
  return {
    part: { id: part.id, level: null, rxPlus: part.usesLevels ? false : null, scores: part.types.map(() => empty), team: null },
    note: part.isTeam ? null : { id: part.id, note: "", feel: "" },
    removed: true,
  };
}

/** Whether a draft says anything at all: a score, a team, a note or a feel. */
export function draftSaysSomething(draft: PartDraft): boolean {
  const scored = draft.part.scores.some((score) => score.value !== null || score.capped === true);
  return scored || draft.part.team !== null || Boolean(draft.note?.note || draft.note?.feel);
}

/**
 * A draft as a saved part reads: its scores shown as the api will show them,
 * its level, badge, note and feel — for its card, and to
 * fill its dialog again when it is opened before the workout is sent. It has
 * no verification and no standing: nothing has seen it yet.
 */
export function draftAsEntryPart(part: RecordablePart, draft: PartDraft): EntryPart {
  const scores = part.types.map((type, position) => {
    const typed = draft.part.scores[position] ?? { value: null, reps: null, capped: null, unit: null };
    const score = {
      scoreTypeId: (type as { scoreTypeId?: string }).scoreTypeId ?? "",
      label: type.label,
      measure: type.measure,
      better: type.better,
      value: typed.value ?? null,
      reps: typed.reps ?? null,
      capped: typed.capped ?? false,
      unit: typed.unit ?? null,
    };
    return { ...score, display: scoreDisplay(score) };
  });
  return {
    id: part.current?.id ?? part.id,
    partId: part.current?.partId ?? part.id,
    title: part.title,
    description: part.current?.description ?? null,
    timeLimitMs: part.timeLimitMs,
    usesLevels: part.usesLevels,
    summary: part.summary,
    level: draft.part.level,
    rxPlus: draft.part.rxPlus ?? false,
    division: part.current?.division ?? null,
    recorded: scores[0] !== undefined && (scores[0].value !== null || scores[0].capped),
    scores,
    note: draft.note?.note || null,
    feel: draft.note?.feel || null,
    verification: null,
    standing: null,
    hiddenAt: null,
    isTeam: part.isTeam,
    teams: [],
  };
}

/** A team's whole result, as its creator changes it or a teammate proposes one. */
export type TeamScoreForm = { level: string | null; rxPlus: boolean | null; scores: ScoreInput[] };

/**
 * A team's score form (0027), named level, rxPlus and scores.0.min and so on,
 * read against its part: the whole result, as the api replaces it.
 */
export function readTeamScore(form: FormData, part: RecordablePart): Read<TeamScoreForm> {
  const scores: ScoreInput[] = [];
  for (const [position, type] of part.types.entries()) {
    const score = readScore(form, `scores.${position}`, type, `Part ${part.label}`);
    if (score.error !== undefined) return score;
    scores.push({
      value: score.value.value ?? null,
      reps: score.value.reps ?? null,
      capped: score.value.capped ?? null,
      unit: score.value.unit ?? null,
    });
  }
  const recorded = scores[0] !== undefined && (scores[0].value !== null || scores[0].capped === true);
  if (!recorded) return { error: `Part ${part.label}: give the team's score.`, field: "scores.0" };
  const level = part.usesLevels ? String(form.get("level") ?? "") || null : null;
  return {
    value: { level, rxPlus: part.usesLevels ? level === "rx" && form.get("rxPlus") !== null : null, scores },
  };
}

/** A board: a part's, or the event's overall ranking when partId is OVERALL. */
export type Board = { partId: string; level: string | null; division: string };

/** The partId an event's overall ranking goes by, in the address as ?part=overall. */
export const OVERALL = "overall";

/** Whether an event has an overall ranking, and a part it counts. */
export function hasOverall(event: SpaceEvent): boolean {
  return event.ranking !== "none" && event.parts.some(isRanked);
}

/** Whether a part has leaderboards: it says so, and it is scored. */
export function isRanked(part: { hasLeaderboard: boolean; scores: { measure: string }[] }): boolean {
  return part.hasLeaderboard && part.scores[0] !== undefined && part.scores[0].measure !== "none";
}

/** Whether an event's overall boards are split by level: a part it counts has levels. */
export function overallHasLevels(event: SpaceEvent): boolean {
  return event.parts.some((part) => isRanked(part) && part.usesLevels);
}

/**
 * Which overall board a page opens on when the address does not say: the
 * viewer's own — the level and division they registered with, or their
 * profile's division and Rx.
 */
export function defaultOverallBoard(event: SpaceEvent, entry: Entry | null, profileDivision: string | null): Board {
  return {
    partId: OVERALL,
    level: overallHasLevels(event) ? (entry?.level ?? "rx") : null,
    division: entry?.division ?? profileDivision ?? "men",
  };
}

/**
 * Which board a page opens on when the address does not say: the viewer's
 * own — the division and level of their result, or their profile's division
 * and Rx — so they land where they stand.
 */
export function defaultBoard(
  part: { id: string; usesLevels: boolean },
  entry: Entry | null,
  profileDivision: string | null,
): Board {
  const mine = entry?.parts.find((copy) => copy.partId === part.id);
  // On a part done in teams, the board of the viewer's first team there.
  const team = mine?.teams?.[0];
  return {
    partId: part.id,
    level: part.usesLevels ? (team?.level ?? mine?.level ?? "rx") : null,
    division: team?.division ?? entry?.division ?? profileDivision ?? "men",
  };
}

/**
 * The first board of a part — or of the overall ranking — that has any
 * results, from the counts a board carries: where to go when the viewer's own
 * is empty.
 */
export function firstBoardWithResults(board: { boards: BoardCount[] }, partId: string): Board | null {
  const first = board.boards[0];
  return first ? { partId, level: first.level, division: first.division } : null;
}

/** How many results one board has, from the counts: for a tab. All's is every division's at its level. */
export function resultsOn(board: { boards: BoardCount[] }, level: string | null, division: string): number {
  return board.boards
    .filter((count) => count.level === level && (division === ALL_DIVISIONS.value || count.division === division))
    .reduce((sum, count) => sum + count.results, 0);
}

/** 1st, 2nd, 3rd, 4th… 11th, 12th, 13th, 21st. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
